
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function OutdoorPowerOutletInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Outdoor Power Outlet Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional outdoor power outlet installation in Fresno, CA. Weatherproof GFCI outlets for patios, decks, gardens, and outdoor living spaces.',
    url: `${siteUrl}/services/lighting-outdoor/outdoor-power-outlet-installation`,
  };

  const features = [
    {
      icon: 'ri-plug-line',
      title: 'GFCI Protected Outlets',
      description: 'Code-required ground fault protection for outdoor safety',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Weatherproof Covers',
      description: 'In-use covers that protect outlets from Fresno weather',
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Patio & Deck Outlets',
      description: 'Convenient power for outdoor entertaining and cooking',
    },
    {
      icon: 'ri-plant-line',
      title: 'Garden Outlets',
      description: 'Power for landscaping tools and water features',
    },
    {
      icon: 'ri-car-line',
      title: 'Garage & Driveway Outlets',
      description: 'Outdoor power for car washing and maintenance',
    },
    {
      icon: 'ri-christmas-line',
      title: 'Holiday Lighting Outlets',
      description: 'Dedicated circuits for seasonal decorations',
    },
  ];

  return (
    <>
      <SEOHead
        title="Outdoor Power Outlet Installation Fresno CA | Weatherproof GFCI"
        description="Professional outdoor power outlet installation in Fresno, CA. Weatherproof GFCI outlets for patios, decks, gardens, and outdoor spaces. Licensed electricians."
        canonical="/services/lighting-outdoor/outdoor-power-outlet-installation"
        keywords="outdoor outlets Fresno, GFCI installation, weatherproof outlets, patio power"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Outdoor Power Outlet Installation — Fresno, CA"
        heroSubtitle="Convenient, safe outdoor power outlets for Fresno patios, decks, and gardens"
        heroImage="https://public.readdy.ai/ai/img_res/61ad78884a6d63a9cb4c82502ebcdd4a.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20weatherproof%20GFCI%20electrical%20outlet%20with%20protective%20cover%20mounted%20on%20exterior%20wall%20of%20California%20home%20professional%20installation%20no%20people%20present&width=700&height=600&seq=outdoor-outlet-fresno-010&orientation=portrait"
        fresnoContent="Add convenience and functionality to your Fresno outdoor spaces with professionally installed weatherproof power outlets. From Tower District patios to Woodward Park decks, we install code-compliant GFCI outlets that provide safe, reliable power for outdoor entertaining, landscaping, holiday decorations, and more. Our licensed electricians ensure proper weatherproofing, ground fault protection, and strategic placement for maximum utility. Enjoy the convenience of outdoor power without extension cords running through your home."
        features={features}
        faqs={[
          {
            question: 'Why do outdoor outlets need GFCI protection in Fresno?',
            answer:
              'GFCI (Ground Fault Circuit Interrupter) protection is required by California electrical code for all outdoor outlets. GFCIs detect electrical imbalances and shut off power within milliseconds, preventing electric shock from water contact, damaged cords, or faulty equipment. This is especially important in Fresno where outdoor outlets may be exposed to sprinkler systems, rain, or morning dew. All our outdoor outlet installations include GFCI protection for maximum safety and code compliance.',
          },
          {
            question: 'How much does outdoor outlet installation cost in Fresno?',
            answer:
              'Outdoor outlet installation in Fresno typically costs $200–$500 per outlet depending on location and complexity. Simple installations on exterior walls near existing circuits start around $200–$300. Outlets requiring new circuit runs from the panel or installations far from existing wiring range from $400–$600. Weatherproof covers and GFCI protection are included. We provide free consultations and detailed quotes for all Fresno projects. Most homeowners find outdoor outlets essential for entertaining and yard maintenance.',
          },
          {
            question: 'Where should outdoor outlets be installed on Fresno homes?',
            answer:
              'For optimal convenience in Fresno, we recommend outdoor outlets on front porches for holiday decorations and power tools, back patios and decks for entertaining and outdoor cooking, near gardens for landscaping equipment and water features, garage exteriors for car washing and maintenance, and pool areas for pumps and cleaning equipment. California code requires outlets within 25 feet of all outdoor living areas. We assess your property and recommend strategic placement during consultation.',
          },
          {
            question: 'What type of weatherproof covers work best in Fresno?',
            answer:
              "For Fresno's climate, we install \"in-use\" weatherproof covers that protect outlets even when cords are plugged in. These covers have spring-loaded doors that close around cords, keeping out dust, rain, and insects. Standard flat covers only protect unused outlets. In-use covers are essential for Fresno where outlets may power landscape lighting, water features, or holiday decorations long-term. We install UV-resistant covers rated for California sun exposure and temperatures exceeding 110°F.",
          },
          {
            question: 'Can outdoor outlets be installed on any exterior wall in Fresno?',
            answer:
              "Most exterior walls can accommodate outdoor outlets, but placement depends on interior wiring access and structural considerations. Stucco and wood siding are straightforward. Brick or stone may require special mounting boxes. We assess your Fresno home's construction and recommend optimal locations that balance convenience, aesthetics, and installation efficiency. We always seal penetrations properly to prevent water intrusion and maintain your home's weather resistance.",
          },
          {
            question: 'Do outdoor outlets require permits in Fresno?',
            answer:
              'Yes, outdoor outlet installations in Fresno typically require electrical permits since they involve modifying your home\'s electrical system. We handle all permit applications and inspections with the City of Fresno, ensuring full compliance with California electrical codes and NEC requirements. Our licensed C-10 electricians ensure all installations meet Fresno building standards. Proper permitting protects your home value, ensures insurance coverage, and guarantees safe, code-compliant work.',
          },
        ]}
      />
    </>
  );
}
